import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { X, Plus, Check } from "lucide-react";
import { categoryService, Category } from "@/api/categoryService";
import { useQuery } from "@tanstack/react-query";

interface CategoryWithRelevance {
  categoryId: string;
  category: {
    title: {
      en: string;
      ar: string;
    };
    slug: string;
  };
  relevanceScore: number;
}

interface EditableCategoriesProps {
  categories: CategoryWithRelevance[];
  prominentCategory?: CategoryWithRelevance;
  onSave: (data: {
    categories: CategoryWithRelevance[];
    prominentCategory?: CategoryWithRelevance;
  }) => void;
  disabled?: boolean;
}

export const EditableCategories: React.FC<EditableCategoriesProps> = ({
  categories,
  prominentCategory,
  onSave,
  disabled = false,
}) => {
  const [localCategories, setLocalCategories] = useState<CategoryWithRelevance[]>(categories);
  const [localProminent, setLocalProminent] = useState<CategoryWithRelevance | undefined>(prominentCategory);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const [relevanceScore, setRelevanceScore] = useState<number>(5);

  // Fetch all available categories
  const { data: allCategories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      const response = await categoryService.getCategories();
      return response || [];
    },
  });

  useEffect(() => {
    setLocalCategories(categories);
    setLocalProminent(prominentCategory);
  }, [categories, prominentCategory]);

  const addCategory = () => {
    const selectedCategory = allCategories.find(cat => cat.id === selectedCategoryId);
    if (!selectedCategory || localCategories.some(cat => cat.categoryId === selectedCategoryId)) {
      return;
    }

    const newCategory: CategoryWithRelevance = {
      categoryId: selectedCategory.id,
      category: {
        title: selectedCategory.title,
        slug: selectedCategory.slug,
      },
      relevanceScore,
    };

    const updatedCategories = [...localCategories, newCategory];
    setLocalCategories(updatedCategories);
    
    // If this is the first category or has the highest relevance score, make it prominent
    const newProminent = updatedCategories.reduce((prev, current) =>
      current.relevanceScore > (prev?.relevanceScore || 0) ? current : prev
    );
    
    setLocalProminent(newProminent);
    
    onSave({
      categories: updatedCategories,
      prominentCategory: newProminent,
    });

    // Reset form
    setSelectedCategoryId("");
    setRelevanceScore(5);
    setShowAddForm(false);
  };

  const removeCategory = (categoryId: string) => {
    const updatedCategories = localCategories.filter(cat => cat.categoryId !== categoryId);
    setLocalCategories(updatedCategories);
    
    // Update prominent category if we removed it
    let newProminent = localProminent;
    if (localProminent?.categoryId === categoryId) {
      newProminent = updatedCategories.reduce((prev, current) =>
        current.relevanceScore > (prev?.relevanceScore || 0) ? current : prev,
        updatedCategories[0]
      );
      setLocalProminent(newProminent);
    }
    
    onSave({
      categories: updatedCategories,
      prominentCategory: newProminent,
    });
  };

  const updateRelevanceScore = (categoryId: string, score: number) => {
    const updatedCategories = localCategories.map(cat =>
      cat.categoryId === categoryId ? { ...cat, relevanceScore: score } : cat
    );
    setLocalCategories(updatedCategories);
    
    // Update prominent category based on highest score
    const newProminent = updatedCategories.reduce((prev, current) =>
      current.relevanceScore > (prev?.relevanceScore || 0) ? current : prev
    );
    setLocalProminent(newProminent);
    
    onSave({
      categories: updatedCategories,
      prominentCategory: newProminent,
    });
  };

  const availableCategories = allCategories.filter(cat =>
    !localCategories.some(localCat => localCat.categoryId === cat.id)
  );

  if (disabled) {
    return (
      <div className="space-y-2">
        {localCategories.map((cat, index) => (
          <Badge
            key={index}
            variant="secondary"
            className={`inline-block mr-2 mb-2 ${
              cat.categoryId === localProminent?.categoryId
                ? 'bg-gradient-to-r from-blue-100 to-purple-100 text-blue-800 border-blue-200'
                : 'bg-gray-100 text-gray-700 border-gray-200'
            }`}
          >
            {cat.category.title.ar}
            <span className="ml-2 opacity-70 text-xs bg-white/50 px-1 rounded">
              {cat.relevanceScore}/10
            </span>
          </Badge>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {localCategories.map((cat, index) => (
          <Card key={index} className="p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Badge
                  className={`${
                    cat.categoryId === localProminent?.categoryId
                      ? 'bg-gradient-to-r from-blue-100 to-purple-100 text-blue-800 border-blue-200'
                      : 'bg-gray-100 text-gray-700 border-gray-200'
                  }`}
                >
                  {cat.category.title.ar}
                  {cat.categoryId === localProminent?.categoryId && (
                    <span className="mr-1 text-xs">(الأساسية)</span>
                  )}
                </Badge>
              </div>
              
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium">النقاط:</label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={cat.relevanceScore}
                  onChange={(e) => updateRelevanceScore(cat.categoryId, parseInt(e.target.value) || 1)}
                  className="w-16 h-8 text-center"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => removeCategory(cat.categoryId)}
                  className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {showAddForm ? (
        <Card className="p-4">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                اختيار فئة
              </label>
              <select
                value={selectedCategoryId}
                onChange={(e) => setSelectedCategoryId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- اختر فئة --</option>
                {availableCategories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.title.ar} ({category.title.en})
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                درجة الصلة (1-10)
              </label>
              <Input
                type="number"
                min="1"
                max="10"
                value={relevanceScore}
                onChange={(e) => setRelevanceScore(parseInt(e.target.value) || 1)}
                className="w-full"
              />
            </div>
            
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={addCategory}
                disabled={!selectedCategoryId}
              >
                <Check className="w-4 h-4 mr-1" />
                إضافة
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setShowAddForm(false);
                  setSelectedCategoryId("");
                  setRelevanceScore(5);
                }}
              >
                <X className="w-4 h-4 mr-1" />
                إلغاء
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowAddForm(true)}
          disabled={availableCategories.length === 0}
          className="flex items-center gap-1"
        >
          <Plus className="w-4 h-4" />
          إضافة فئة
        </Button>
      )}
    </div>
  );
};